import { z } from 'zod';

/**
 * User-defined ("custom" / dynamic) fields on items. A tenant admin defines a
 * set of field definitions; each item then stores values keyed by the field
 * `code` in its `customFields` JSON column. The definitions drive rendering on
 * the item form, the list columns, the toolbar filters, and CSV import/export.
 */
export const CustomFieldTypeSchema = z.enum([
  'text',
  'longtext',
  'number',
  'select',
  'multiselect',
  'boolean',
  'date',
]);
export type CustomFieldType = z.infer<typeof CustomFieldTypeSchema>;

/**
 * Field codes double as JSON keys and CSV headers (`cf_<code>`), so they're
 * constrained to a safe lower_snake_case identifier.
 */
const fieldCode = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z][a-z0-9_]*$/);

const optionList = z.array(z.string().min(1).max(120)).max(100);

export const CustomFieldDefinitionSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  code: z.string().min(1).max(64),
  label: z.string().min(1).max(200),
  fieldType: CustomFieldTypeSchema,
  /** Allowed values for select / multiselect; null for the other types. */
  options: z.array(z.string()).nullable(),
  isRequired: z.boolean(),
  showInList: z.boolean(),
  /**
   * Show this field to CUSTOMERS — the storefront product page and the app.
   * Defaults on, because a shop that defines a field expects to see it; turn it
   * off for anything that belongs to the office (shelf, supplier note).
   */
  showPublic: z.boolean(),
  filterable: z.boolean(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type CustomFieldDefinition = z.infer<typeof CustomFieldDefinitionSchema>;

export const CustomFieldDefinitionCreateSchema = z
  .object({
    code: fieldCode,
    label: z.string().min(1).max(200),
    fieldType: CustomFieldTypeSchema,
    options: optionList.optional(),
    isRequired: z.boolean().optional(),
    showInList: z.boolean().optional(),
    showPublic: z.boolean().optional(),
    filterable: z.boolean().optional(),
    sortOrder: z.number().int().optional(),
  })
  .superRefine((v, ctx) => {
    if ((v.fieldType === 'select' || v.fieldType === 'multiselect') && (!v.options || v.options.length === 0)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['options'], message: 'options_required' });
    }
  });
export type CustomFieldDefinitionCreate = z.infer<typeof CustomFieldDefinitionCreateSchema>;

/**
 * `code` and `fieldType` are intentionally immutable post-create — changing
 * the type would orphan already-stored values, and the code is the storage
 * key. Everything else (label, options, flags, order, active) is editable.
 */
export const CustomFieldDefinitionUpdateSchema = z.object({
  label: z.string().min(1).max(200).optional(),
  options: optionList.nullable().optional(),
  isRequired: z.boolean().optional(),
  showInList: z.boolean().optional(),
  showPublic: z.boolean().optional(),
  filterable: z.boolean().optional(),
  sortOrder: z.number().int().optional(),
  isActive: z.boolean().optional(),
});
export type CustomFieldDefinitionUpdate = z.infer<typeof CustomFieldDefinitionUpdateSchema>;
